
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import CategoriesActions from '@/modules/CouriersCategories/components/CategoriesActions/index.vue'

// realisations
import { couriers } from '@/core/realisations/couriers.realisation'

// types
import { VehicleType, VehicleTypePayload } from '@/core/types/couriers.types'

// constants
import { CouriersCategoriesColumns } from '@/core/constants/columns/CouriersCategories.columns'

// composable
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'table-scroll-wrapper': TableScrollWrapper,
        'the-table': TheTable,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'categories-actions': CategoriesActions,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            is_failed: false,
        })

        const data_state = reactive({
            vehicle_types: new DataState<VehicleType>({}),
        })

        const actions_state = reactive({
            is_actions_opened: false,
            is_actions_loading: false,
            is_create: false,
            is_update: false,
            is_delete: false,
            initial_value: null as VehicleType | null,
        })

        const loadVehicleTypes = async (page: number) => {
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true

                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = response.data
                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
                data_state.vehicle_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.vehicle_types.failure.catchError(error)

                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_failed = true
                }
            } finally {
                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadVehicleTypes(1)
        })

        const createCategory = async (category_payload: VehicleTypePayload) => {
            try {
                actions_state.is_actions_loading = true
                await couriers.createCategory({ ...category_payload })
                await loadVehicleTypes(1)

                closeModal()
            } catch (error) {
                console.error(error)
            }
        }

        const updateCategory = async (category_payload: VehicleTypePayload) => {
            try {
                if (!actions_state.initial_value) return

                actions_state.is_actions_loading = true

                await couriers.updateCategory(actions_state.initial_value.id, {
                    ...category_payload,
                })
                await loadVehicleTypes(1)

                closeModal()
            } catch (error) {
                console.error(error)
            }
        }

        const deleteCategory = async (id: number) => {
            try {
                actions_state.is_actions_loading = true

                await couriers.deleteCategory(id)
                await loadVehicleTypes(1)

                closeModal()
            } catch (error) {
                console.error(error)
            }
        }

        const startCreate = () => {
            actions_state.is_create = true
            actions_state.is_actions_opened = true
        }

        const startDelete = (category: VehicleType) => {
            actions_state.initial_value = { ...category }

            actions_state.is_delete = true
            actions_state.is_actions_opened = true
        }

        const startUpdate = (category: VehicleType) => {
            actions_state.initial_value = { ...category }

            actions_state.is_update = true
            actions_state.is_actions_opened = true
        }

        const closeModal = () => {
            actions_state.initial_value = null

            actions_state.is_actions_opened = false
            actions_state.is_actions_loading = false
            actions_state.is_delete = false
            actions_state.is_create = false
            actions_state.is_update = false
        }

        return {
            state,
            data_state,
            actions_state,
            CouriersCategoriesColumns,
            loadVehicleTypes,
            createCategory,
            updateCategory,
            deleteCategory,
            startCreate,
            startDelete,
            startUpdate,
            closeModal,
        }
    },
})
